import React, { useState, useEffect, forwardRef, memo } from 'react';
import { injectIntl } from 'react-intl';
import { Form } from 'antd';
import classnames from 'classnames';

import { getAllAreas } from 'services/cities';
import { getAreaName, getCityName } from 'utils/helpers';
import { useModal } from 'contexts/modalProvider.context';

import BRButton from 'components/BRButton/BRButton';
import BRCityAreaCollapse from 'components/BRCityAreaCollapse/BRCityAreaCollapse';

import './BRCityArea.less';

export const AVAILABILITY = {
  DROPOFF: 'dropOffAvailability',
  PICKUP: 'pickupAvailability'
};

export const CONTEXT = {
  dropOffAvailability: 'dropoff',
  pickupAvailability: 'pickup'
};

const BRCityArea = (props) => {
  const {
    formRef,
    intl,
    name,
    areas = [],
    setAreas,
    availability,
    formItemClassName = '',
    onChange,
    disabled,
    dropdownWidth,
    hasFeedback,
    cityID,
    initialValue,
    addressInitialValue,
    googleMapInitialValue,
    setCities,
    isMapUsed,
    setIsAreaRequired = () => {},
    setIsMapUsed = () => {},
    manualSelectionName,
    setUserPosition = () => {},
    shouldSetAreaInitialValue = true,
    ...restOfProps
  } = props;

  const [selectedCityArea, setSelectedCityArea] = useState({});
  const [isAllAreasLoading, setIsAreasLoading] = useState(false);
  const [allAreas, setAllAreas] = useState(areas);
  const [isAreaDisabled, setIsAreaDisabled] = useState(false);

  const { openModal } = useModal();

  const setCityAreaInitialValues = (addressInitialValue = {}) => {
    const { city, districtId, district } = addressInitialValue;

    if (city) {
      const { districts, ...cityObj } =
        allAreas.find((area) => [city, city?._id].includes(area?.cityId)) || {};

      const areaObj =
        districts?.find((area) =>
          [districtId, district?._id].includes(area?.districtId)
        ) || {};

      setSelectedCityArea({
        ...cityObj,
        ...areaObj
      });
    }
  };

  const handleOpenCityAreaModal = (fromChangeButton) => {
    openModal(BRCityAreaCollapse, {
      name,
      formRef,
      availability: availability || AVAILABILITY.DROPOFF,
      setCities,
      updateAddress: () => {},
      selectedCityArea,
      setSelectedCityArea,
      setCityArea: () => {},
      setUserPosition: () => {},
      fetchedAreas: allAreas,
      onChange,
      setIsAreaDisabled,
      setIsMapUsed,
      fromChangeButton,
      manualSelectionName,
      ...restOfProps
    });
  };

  const handleCityAreaButtonClick = () => {
    if (!isMapUsed) {
      handleOpenCityAreaModal();
    }
  };

  const onClickChangeArea = () => {
    setIsMapUsed(false);
    handleOpenCityAreaModal(true);
  };

  const renderLabel = () => {
    const cityName = getCityName(selectedCityArea);

    const areaName = getAreaName({
      area: selectedCityArea
    });

    return cityName ? (
      <>
        <span className="br-city-area__field">
          {!isMapUsed && (
            <span className="br-city-area__area-name">{areaName}</span>
          )}
          <span className="br-city-area__city-name">{cityName}</span>
        </span>
        {isMapUsed && (
          <BRButton
            type="link-color"
            label="Change"
            onClick={onClickChangeArea}
          />
        )}
      </>
    ) : (
      <span className="br-city-area__placeholder">
        {intl.formatMessage({
          id: `form.area_select_placeholder`
        })}
      </span>
    );
  };

  useEffect(() => {
    async function fetchData() {
      setIsAreasLoading(true);
      const allAreas =
        (await getAllAreas({ context: CONTEXT[availability] })) || [];
      setAllAreas(allAreas);
      setCities?.(allAreas);
      setAreas?.(allAreas);
      setIsAreasLoading(false);
    }
    if (!allAreas?.length) {
      fetchData();
    }
  }, [availability]);

  useEffect(() => {
    const address = formRef?.current?.getFieldValue?.('address');
    if (
      allAreas?.length &&
      address &&
      !Object.keys(selectedCityArea).length &&
      shouldSetAreaInitialValue
    ) {
      setCityAreaInitialValues(address);
    }
  }, [allAreas, formRef?.current?.getFieldValue?.('address')]);

  useEffect(() => {
    if (googleMapInitialValue) {
      setSelectedCityArea(googleMapInitialValue);
      setIsAreaDisabled(true);
    }
  }, [googleMapInitialValue]);

  useEffect(() => {
    if (isMapUsed) {
      setIsAreaDisabled(true);
    }
  }, [isMapUsed]);

  return (
    <Form.Item
      name={name || 'districtId'}
      rules={[{ required: !isMapUsed }]}
      className={classnames('br-forms-new-input br-form-city-area', {
        'br-form-city-area__dimmed': isAreaDisabled
      })}
      label={intl.formatMessage({
        id: `location_form_labels.${!isMapUsed ? 'area' : 'city'}`
      })}
      initialValue={initialValue}
      hasFeedback
    >
      <BRButton
        className="br-city-area__btn"
        label={renderLabel()}
        loading={isAllAreasLoading}
        onClick={handleCityAreaButtonClick}
      />
    </Form.Item>
  );
};

export default injectIntl(memo(BRCityArea));
